// ===== 1. Imports =====
import React, { useState, useEffect, useMemo, useRef } from "react";
import Modal from "../../components/Modal";
import { db } from "../../firebaseConfig";
import {
  collection,
  addDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from "firebase/firestore";
import {
  Eye,
  PlusCircle,
  Maximize2,
  Minimize2,
  Search,
  Save,
  FileText,
  Trash,
} from "lucide-react";
import { fmt, formatStatus } from "./utils/formatters";

// ===== 2. Helpers =====
const getNextNumber = (posList) => {
  const numbers = posList
    .map((p) => parseInt(p.number))
    .filter((n) => !isNaN(n));
  const next = numbers.length > 0 ? Math.max(...numbers) + 1 : 1;
  return String(next).padStart(4, "0");
};

// ===== 3. Componente Principal =====
export default function POPanel({
  projectCode,
  pos,
  setPos,
  suppliers = [],
  accounts = [], // subcuentas de presupuesto [{code, name}, ...]
  userRole,
  userName,
  department, // desde sesión
  setSelectedPO,
  setInvoicesIndex, // (se mantiene por compatibilidad; no se usa aquí)
  onFullscreen,
  isFullscreen = false,
}) {
  // ===== 3.1 Estados =====
  const [departments, setDepartments] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [deptFilter, setDeptFilter] = useState("all");

  const [showPOModal, setShowPOModal] = useState(false);

  const [showDrafts, setShowDrafts] = useState(false);
  const [drafts, setDrafts] = useState(
    JSON.parse(localStorage.getItem("po_drafts") || "[]")
  );

  const [formPO, setFormPO] = useState({
    department: "",
    supplier: "",
    supplierSearch: "",
    type: "Compra",
    description: "",
    notes: "",
    conditions: "",
    items: [{ subaccount: "", description: "", base: "", iva: 21, irpf: 0 }],
  });

  // Autocompletado proveedor
  const [showSupplierList, setShowSupplierList] = useState(false);
  const supplierBoxRef = useRef(null);

  // ===== 3.2 Firestore: departamentos + POs =====
  useEffect(() => {
    if (!projectCode) return;

    // Departamentos
    const deptRef = collection(db, "projects", projectCode, "departamentos");
    const unsubDepts = onSnapshot(deptRef, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) =>
        String(a.name).localeCompare(b.name, "es", { sensitivity: "base" })
      );
      setDepartments(list);
    });

    // POs
    const posRef = query(
      collection(db, "projects", projectCode, "pos"),
      orderBy("createdAt", "asc")
    );
    const unsubPOs = onSnapshot(posRef, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setPos(list);
    });

    return () => {
      unsubDepts();
      unsubPOs();
    };
  }, [projectCode, setPos]);

  // ===== 3.3 Autocompletar proveedor =====
  const filteredSuppliers = useMemo(() => {
    const t = (formPO.supplierSearch || "").toLowerCase().trim();
    if (!t) return [];
    return suppliers
      .filter((s) => {
        const code = String(s.code || "").padStart(3, "0");
        return (
          s.name?.toLowerCase().includes(t) ||
          code.includes(t) ||
          s.cif?.toLowerCase().includes(t)
        );
      })
      .slice(0, 8);
  }, [formPO.supplierSearch, suppliers]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        supplierBoxRef.current &&
        !supplierBoxRef.current.contains(e.target)
      ) {
        setShowSupplierList(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // ===== 3.4 Auto asignar departamento según rol =====
  useEffect(() => {
    if (["HOD", "Coordinator", "Crew"].includes(userRole) && department) {
      setFormPO((f) => ({ ...f, department }));
    }
  }, [userRole, department]);

  // ===== 3.5 Filtrado de POs en tabla =====
  const filteredPOs = useMemo(() => {
    let list = [...pos];
    if (statusFilter === "approved")
      list = list.filter((p) => p.status === "Aprobada");
    if (deptFilter !== "all")
      list = list.filter((p) => p.department === deptFilter);
    if (search.trim()) {
      const t = search.toLowerCase();
      list = list.filter(
        (p) =>
          (p.number || "").toLowerCase().includes(t) ||
          (p.supplier || "").toLowerCase().includes(t) ||
          (p.description || "").toLowerCase().includes(t)
      );
    }
    return list;
  }, [pos, search, statusFilter, deptFilter]);

  // ===== 3.6 Borradores =====
  const handleSaveDraft = () => {
    const cleanDraft = {
      ...formPO,
      items: formPO.items.map((i) => ({
        subaccount: i.subaccount || "",
        description: i.description || "",
        base: i.base,
        iva: i.iva,
        irpf: i.irpf,
      })),
    };
    const updated = [...drafts, cleanDraft];
    setDrafts(updated);
    localStorage.setItem("po_drafts", JSON.stringify(updated));
    alert("Borrador guardado");
  };

  const handleDeleteDraft = (index) => {
    const updated = drafts.filter((_, i) => i !== index);
    setDrafts(updated);
    localStorage.setItem("po_drafts", JSON.stringify(updated));
  };

  // ===== 3.7 Crear PO =====
  const handleSubmitPO = async (e) => {
    e.preventDefault();
    if (!projectCode) return;

    if (!formPO.supplier || !formPO.department) {
      alert("Proveedor y departamento son obligatorios");
      return;
    }

    const hasAnyBase = formPO.items.some((i) => Number(i.base) > 0);
    if (!hasAnyBase) {
      const go = window.confirm(
        "No has introducido importes en los ítems.\n¿Quieres crear la PO igualmente?"
      );
      if (!go) return;
    }

    try {
      const newNumber = getNextNumber(pos);
      const data = {
        number: newNumber,
        supplier: formPO.supplier,
        department: formPO.department,
        description: formPO.description || "",
        notes: formPO.notes || "",
        conditions: formPO.conditions || "",
        type: formPO.type,
        items: formPO.items.map((i) => ({
          subaccount: i.subaccount || "",
          description: i.description || "",
          base: Number(i.base) || 0,
          iva: Number(i.iva) || 0,
          irpf: Number(i.irpf) || 0,
        })),
        approvals: {
          requiresHod: true,
          hod: { approved: false, name: "", date: "" },
          pm: { approved: false, name: "", date: "" },
          controller: { approved: false, name: "", date: "" },
        },
        status: "Pendiente",
        createdAt: serverTimestamp(),
        createdBy: userName,
        modified: false,
        version: 1,
      };

      await addDoc(collection(db, "projects", projectCode, "pos"), data);
      alert("PO creada correctamente");
      setShowPOModal(false);

      // reset del formulario conservando el departamento si es HOD/Coordinator/Crew
      setFormPO({
        department: ["HOD", "Coordinator", "Crew"].includes(userRole)
          ? department
          : "",
        supplier: "",
        supplierSearch: "",
        type: "Compra",
        description: "",
        notes: "",
        conditions: "",
        items: [
          { subaccount: "", description: "", base: "", iva: 21, irpf: 0 },
        ],
      });
    } catch (err) {
      console.error("Error creando PO:", err);
      alert("Error al crear la PO. Revisa la consola.");
    }
  };

  // ===== 3.8 Render =====
  const budgetSubaccounts = accounts; // [{code, name}]

  return (
    <div className="card" style={{ padding: 20 }}>
      {/* --- Cabecera --- */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h2 style={{ margin: 0 }}>Panel POs</h2>
        <div style={{ display: "flex", gap: 8 }}>
          {/* Nueva PO */}
          <button
            className="button button--primary"
            title="Nueva PO"
            onClick={() => setShowPOModal(true)}
            style={{
              height: 36,
              padding: "0 14px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PlusCircle size={18} />
          </button>

          {/* Pantalla completa */}
          <button
            className="button"
            onClick={onFullscreen}
            title={
              isFullscreen ? "Salir de pantalla completa" : "Pantalla completa"
            }
            style={{
              height: 36,
              padding: "0 14px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
        </div>
      </div>

      {/* --- Resumen de POs --- */}
      {(() => {
        // Filtramos según rol
        let visiblePOs = pos;

        if (["Crew", "Coordinator"].includes(userRole)) {
          visiblePOs = pos.filter((p) => p.createdBy === userName);
        } else if (userRole === "HOD" && department) {
          visiblePOs = pos.filter((p) => p.department === department);
        }

        const pending = visiblePOs.filter(
          (p) => p.status === "Pendiente"
        ).length;
        const approved = visiblePOs.filter(
          (p) => p.status === "Aprobada"
        ).length;
        const total = visiblePOs.length;

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 16,
              marginTop: 10,
              marginBottom: 8,
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            <span>
              Pendientes:{" "}
              <strong style={{ color: "#eab308" }}>{pending}</strong>
            </span>
            <span>
              Aprobadas:{" "}
              <strong style={{ color: "#16a34a" }}>{approved}</strong>
            </span>
            <span style={{ color: "var(--muted)" }}>
              Total: <strong>{total}</strong>
            </span>
          </div>
        );
      })()}

      {/* --- Filtros tabla --- */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 8,
          margin: "12px 0",
        }}
      >
        <input
          type="text"
          placeholder="Buscar por Nº, proveedor o descripción…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todas</option>
          <option value="approved">Aprobadas</option>
        </select>
        <select
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
        >
          <option value="all">Todos los departamentos</option>
          {departments.map((d) => (
            <option key={d.id} value={d.name}>
              {d.name}
            </option>
          ))}
        </select>
      </div>

      {/* --- Tabla de POs --- */}
      <div style={{ overflowX: "auto" }}>
        <table className="users-table">
          <thead>
            <tr>
              <th>N.º PO</th>
              <th>Proveedor</th>
              <th>Departamento</th>
              <th>Descripción</th>
              <th>Importe</th>
              <th>Estado</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredPOs.map((po) => {
              const base = (po.items || []).reduce(
                (s, i) => s + (Number(i.base) || 0),
                0
              );
              const { bg, label } = formatStatus(po.status);
              return (
                <tr key={po.id}>
                  <td>{po.number}</td>
                  <td>{po.supplier}</td>
                  <td>{po.department}</td>
                  <td className="truncate">{po.description}</td>
                  <td style={{ textAlign: "right" }}>{fmt(base)}</td>
                  <td style={{ textAlign: "center" }}>
                    <span
                      style={{
                        background: bg,
                        color: "#fff",
                        padding: "2px 8px",
                        borderRadius: 6,
                      }}
                    >
                      {label}
                    </span>
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <button
                      className="button button--ghost"
                      onClick={() => setSelectedPO(po)}
                      title="Ver detalle"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              );
            })}
            {filteredPOs.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  style={{ textAlign: "center", color: "var(--muted)" }}
                >
                  No hay POs que coincidan con el filtro.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* --- Modal: Nueva PO --- */}
      {showPOModal && (
        <Modal
          title="Nueva PO"
          onClose={() => setShowPOModal(false)}
          style={{ maxWidth: "1100px" }}
        >
          <form className="stack" onSubmit={handleSubmitPO}>
            {/* === Información general === */}
            <h3>Información</h3>
            <div className="form-grid">
              <div className="field">
                <label>Departamento</label>
                <select
                  value={formPO.department}
                  onChange={(e) =>
                    setFormPO({ ...formPO, department: e.target.value })
                  }
                  disabled={["HOD", "Coordinator", "Crew"].includes(userRole)}
                  required
                >
                  <option value="">Seleccionar…</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.name}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label>Creado por</label>
                <input type="text" value={userName} readOnly />
              </div>
            </div>

            {/* === Proveedor (autocompletado) === */}
            <h3>Proveedor</h3>
            <div
              className="form-grid"
              style={{ gridTemplateColumns: "1fr 1fr" }}
            >
              <div className="field field--span2" ref={supplierBoxRef}>
                <label>Buscar proveedor</label>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    placeholder="Escribe nombre, código o CIF…"
                    value={formPO.supplierSearch}
                    onFocus={() => setShowSupplierList(true)}
                    onChange={(e) =>
                      setFormPO({
                        ...formPO,
                        supplierSearch: e.target.value,
                        supplier: "",
                      })
                    }
                  />
                  {/* Dropdown resultados */}
                  {showSupplierList && formPO.supplierSearch && (
                    <div
                      style={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        right: 0,
                        background: "#fff",
                        border: "1px solid var(--ring)",
                        borderRadius: 8,
                        boxShadow: "0 8px 18px rgba(0,0,0,0.08)",
                        zIndex: 20,
                        maxHeight: 220,
                        overflowY: "auto",
                      }}
                    >
                      {filteredSuppliers.length > 0 ? (
                        filteredSuppliers.map((s) => (
                          <div
                            key={s.id}
                            onClick={() => {
                              setFormPO({
                                ...formPO,
                                supplier: s.name,
                                supplierSearch: `${String(
                                  s.code || ""
                                ).padStart(3, "0")} - ${s.name}${
                                  s.cif ? ` (${s.cif})` : ""
                                }`,
                              });
                              setShowSupplierList(false);
                            }}
                            style={{
                              padding: "8px 10px",
                              cursor: "pointer",
                              borderBottom: "1px solid #F3F4F6",
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.background = "#F9FAFB")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.background = "#FFF")
                            }
                          >
                            <Search size={14} />
                            <div style={{ display: "flex", gap: 8 }}>
                              <strong>
                                {String(s.code || "").padStart(3, "0")}
                              </strong>
                              <span>{s.name}</span>
                              {s.cif && (
                                <span
                                  style={{ color: "#6b7280", fontSize: 13 }}
                                >
                                  ({s.cif})
                                </span>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div
                          style={{
                            padding: 10,
                            color: "#9ca3af",
                            fontSize: 13,
                          }}
                        >
                          Sin resultados
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Chip del proveedor elegido */}
                {formPO.supplier && (
                  <div
                    className="card"
                    style={{
                      marginTop: 8,
                      padding: "8px 10px",
                      display: "inline-flex",
                      gap: 10,
                      alignItems: "center",
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>{formPO.supplier}</span>
                    <button
                      type="button"
                      className="button button--ghost"
                      onClick={() =>
                        setFormPO({
                          ...formPO,
                          supplier: "",
                          supplierSearch: "",
                        })
                      }
                      title="Quitar proveedor"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* === Datos de la PO === */}
            <h3>Datos de la PO</h3>
            <div className="form-grid">
              <div className="field">
                <label>Tipo de PO</label>
                <select
                  value={formPO.type}
                  onChange={(e) =>
                    setFormPO({ ...formPO, type: e.target.value })
                  }
                >
                  <option>Compra</option>
                  <option>Alquiler</option>
                  <option>Servicio</option>
                </select>
              </div>
              <div className="field field--span2">
                <label>Descripción general</label>
                <textarea
                  value={formPO.description}
                  onChange={(e) =>
                    setFormPO({ ...formPO, description: e.target.value })
                  }
                  rows={2}
                />
              </div>
            </div>

            {/* === Ítems === */}
            <h3>Ítems</h3>
            <div style={{ overflowX: "auto" }}>
              <table
                className="users-table"
                style={{
                  width: "100%",
                  tableLayout: "fixed",
                  borderCollapse: "collapse",
                  textAlign: "right",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ width: "12%" }}>Código presupuesto</th>
                    <th style={{ width: "34%", textAlign: "left" }}>
                      Descripción
                    </th>
                    <th style={{ width: "10%" }}>Base</th>
                    <th style={{ width: "8%" }}>IVA (%)</th>
                    <th style={{ width: "10%" }}>IVA €</th>
                    <th style={{ width: "8%" }}>IRPF (%)</th>
                    <th style={{ width: "10%" }}>IRPF €</th>
                    <th style={{ width: "12%" }}>Total</th>
                    <th style={{ width: "4%" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {formPO.items.map((item, idx) => {
                    const base = Number(item.base) || 0;
                    const ivaPct = Number(item.iva) || 0;
                    const irpfPct = Number(item.irpf) || 0;
                    const ivaVal = (base * ivaPct) / 100;
                    const irpfVal = (base * irpfPct) / 100;
                    const total = base + ivaVal - irpfVal;

                    return (
                      <tr key={idx}>
                        <td>
                          <select
                            value={item.subaccount || ""}
                            onChange={(e) => {
                              const updated = [...formPO.items];
                              updated[idx].subaccount = e.target.value;
                              setFormPO({ ...formPO, items: updated });
                            }}
                            style={{ width: "100%" }}
                          >
                            <option value="">—</option>
                            {budgetSubaccounts.map((s) => (
                              <option key={s.code} value={s.code}>
                                {s.code} · {s.name}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td style={{ textAlign: "left" }}>
                          <input
                            value={item.description || ""}
                            onChange={(e) => {
                              const updated = [...formPO.items];
                              updated[idx].description = e.target.value;
                              setFormPO({ ...formPO, items: updated });
                            }}
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            value={item.base ?? ""}
                            onChange={(e) => {
                              const updated = [...formPO.items];
                              updated[idx].base = e.target.value;
                              setFormPO({ ...formPO, items: updated });
                            }}
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            value={item.iva ?? 21}
                            onChange={(e) => {
                              const updated = [...formPO.items];
                              updated[idx].iva = e.target.value;
                              setFormPO({ ...formPO, items: updated });
                            }}
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td>{fmt(ivaVal)}</td>
                        <td>
                          <input
                            type="number"
                            step="0.01"
                            value={item.irpf ?? 0}
                            onChange={(e) => {
                              const updated = [...formPO.items];
                              updated[idx].irpf = e.target.value;
                              setFormPO({ ...formPO, items: updated });
                            }}
                            style={{ width: "100%" }}
                          />
                        </td>
                        <td>{fmt(irpfVal)}</td>
                        <td>
                          <strong>{fmt(total)}</strong>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <button
                            type="button"
                            className="button button--ghost"
                            onClick={() => {
                              const updated = [...formPO.items];
                              updated.splice(idx, 1);
                              setFormPO({ ...formPO, items: updated });
                            }}
                            title="Eliminar ítem"
                            style={{
                              padding: "4px 6px",
                              color: "var(--danger)",
                              fontWeight: 700,
                            }}
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <button
              type="button"
              className="button"
              onClick={() =>
                setFormPO((f) => ({
                  ...f,
                  items: [
                    ...f.items,
                    {
                      subaccount: "",
                      description: "",
                      base: "",
                      iva: 21,
                      irpf: 0,
                    },
                  ],
                }))
              }
              style={{ marginTop: 12 }}
            >
              + Añadir ítem
            </button>

            {/* === Notas y condiciones === */}
            <h3>Notas y condiciones</h3>
            <div className="form-grid">
              <div className="field field--span2">
                <label>Notas</label>
                <textarea
                  rows={3}
                  value={formPO.notes}
                  onChange={(e) =>
                    setFormPO({ ...formPO, notes: e.target.value })
                  }
                />
              </div>
              <div className="field field--span2">
                <label>Condiciones de pago</label>
                <textarea
                  rows={3}
                  value={formPO.conditions}
                  onChange={(e) =>
                    setFormPO({ ...formPO, conditions: e.target.value })
                  }
                />
              </div>
            </div>

            {/* === Botonera y borradores === */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                marginTop: 16,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {drafts.length > 0 && (
                <button
                  type="button"
                  className="button"
                  onClick={() => setShowDrafts(true)}
                  title="Ver borradores"
                >
                  Ver borradores ({drafts.length})
                </button>
              )}

              <button
                type="button"
                className="button"
                onClick={handleSaveDraft}
                title="Guardar en borradores (local)"
              >
                Guardar borrador
              </button>

              <button
                type="button"
                className="button"
                onClick={() => setShowPOModal(false)}
              >
                Cancelar
              </button>

              <button type="submit" className="button button--primary">
                Enviar PO
              </button>
            </div>

            {/* === Modal de borradores === */}
            {showDrafts && (
              <Modal
                title="Borradores guardados"
                onClose={() => setShowDrafts(false)}
              >
                <div className="stack">
                  {drafts.length === 0 && (
                    <p style={{ color: "var(--muted)" }}>
                      No hay borradores guardados.
                    </p>
                  )}
                  {drafts.map((d, i) => (
                    <div
                      key={i}
                      className="card"
                      style={{
                        padding: 12,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <div>
                        <strong>{d.description || "(sin descripción)"}</strong>
                        <div style={{ fontSize: 13, color: "var(--muted)" }}>
                          {d.supplier || "Proveedor no asignado"}
                        </div>
                        {Array.isArray(d.items) && d.items.length > 0 && (
                          <div style={{ fontSize: 12, marginTop: 4 }}>
                            Ítems: {d.items.length}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          className="button button--primary"
                          onClick={() => {
                            setFormPO(d);
                            setShowDrafts(false);
                          }}
                        >
                          Cargar
                        </button>
                        <button
                          className="button button--ghost"
                          onClick={() => handleDeleteDraft(i)}
                          title="Eliminar borrador"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </Modal>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
}
